import { useTheme } from '@emotion/react';
import { IconClose, IconArrowsCircle, IconDeleteOutlined } from '@hubble/icons/web';
import axios from 'axios';
import React__default, { useState, useRef, useEffect } from 'react';
import { Flex } from '../Flex/index.js';
import { Message } from '../forms/Message/index.js';
import { LimitTextLength } from '../LimitTextLength/index.js';
import { Spacer } from '../primitives/Spacer/index.js';
import { CircularProgress } from './CircularProgress.js';
import { FileIcon } from './FileIcon.js';
import { getFileUploaderCopy } from './i18n.js';
import { FileWrapper, FlexWrapper, TextDiv, WideDeviceContent, IconButton } from './styles.js';
import { needsToBeUploaded, isStoredFile } from './utils.js';
import { jsxs, jsx } from '@emotion/react/jsx-runtime';

const CancelToken = axios.CancelToken;
const TIME_TO_HIDE_LOADING_PROGRESS = 3000;
const File = ({
  ['data-testid']: dataTestId,
  error,
  file,
  maxSize,
  message,
  onRemove,
  onUpload,
  uploadFile,
  readOnly,
  ...spacerOptions
}) => {
  const {
    translate
  } = useTheme();
  const {
    ARIA_LABELS,
    CANCELLED,
    FAILED
  } = getFileUploaderCopy(translate);
  const [progress, setProgress] = useState(needsToBeUploaded(file, error) ? 0 : isStoredFile(file) ? 100 : null);
  const [showLoadingProgress, setShowLoadingProgress] = useState(false);
  const [innerErrorMessage, setInnerErrorMessage] = useState('');
  const timer = useRef(null);
  const source = useRef(CancelToken.source());
  useEffect(() => {
    if (!isStoredFile(file) && !error) {
      handleUpload(file);
    }
    return () => {
      clearTimeout(timer.current);
    };
  }, []);
  const fileName = file.name;
  const extension = fileName.split('.').pop();
  const handleCancel = () => {
    setProgress(null);
    source.current.cancel(CANCELLED);
    source.current = CancelToken.source();
  };
  const loading = progress !== null && progress >= 0 && progress < 100;
  const hasError = Boolean(innerErrorMessage) || Boolean(error);
  const handleUpload = file => {
    if (file) {
      setInnerErrorMessage('');
      setShowLoadingProgress(true);
      const axiosConfig = {
        cancelToken: source.current.token,
        onUploadProgress: progressEvent => {
          const percentCompleted = Math.round(progressEvent.loaded * 100 / progressEvent.total);
          setProgress(percentCompleted === 100 ? 99 : percentCompleted);
        }
      };
      uploadFile(file, axiosConfig).then(uploadedFile => {
        setProgress(100);
        timer.current = setTimeout(() => {
          setShowLoadingProgress(false);
        }, TIME_TO_HIDE_LOADING_PROGRESS);
        onUpload(uploadedFile);
      }).catch(err => {
        if (axios.isCancel(err)) {
          console.log(CANCELLED, err.message);
          setInnerErrorMessage(CANCELLED);
        } else {
          console.error(err);
          setInnerErrorMessage(FAILED);
        }
        onUpload(null);
        setProgress(null);
      });
    }
  };
  return jsxs(Spacer, {
    as: "div",
    ...spacerOptions,
    children: [jsx(FileWrapper, {
      "data-testid": dataTestId,
      readOnly: readOnly,
      error: hasError,
      children: jsxs(FlexWrapper, {
        children: [jsx(Flex, {
          alignItems: "center",
          minWidth: 0,
          children: jsx(FileIcon, {
            extension: extension
          })
        }), jsx(Flex, {
          flex: "1",
          minWidth: 0,
          children: jsx(TextDiv, {
            as: "div",
            children: jsx(LimitTextLength, {
              text: fileName,
              middleTrim: true,
              addParentheses: false
            })
          })
        }), showLoadingProgress && !hasError && jsxs(React__default.Fragment, {
          children: [jsxs(WideDeviceContent, {
            as: "div",
            children: [progress, "%"]
          }), jsx("div", {
            children: jsx(CircularProgress, {
              percentage: progress,
              size: 20
            })
          })]
        }), !readOnly && jsx(React__default.Fragment, {
          children: loading ? jsx(IconButton, {
            "aria-label": ARIA_LABELS.CLOSE,
            onClick: handleCancel,
            "data-testid": "close-button",
            children: jsx(IconClose, {})
          }) : jsxs(React__default.Fragment, {
            children: [Boolean(innerErrorMessage) && jsx(IconButton, {
              "aria-label": ARIA_LABELS.RETRY,
              onClick: () => handleUpload(file),
              "data-testid": "retry-button",
              children: jsx(IconArrowsCircle, {})
            }), jsx(IconButton, {
              "aria-label": ARIA_LABELS.REMOVE,
              onClick: onRemove,
              "data-testid": "remove-button",
              children: innerErrorMessage ? jsx(IconClose, {}) : jsx(IconDeleteOutlined, {})
            })]
          })
        })]
      })
    }), hasError && jsx(Message, {
      error: innerErrorMessage || error,
      "data-testid": `${dataTestId}-message-text`
    })]
  });
};

export { File };
